import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { isPresent, isBlank } from '@fretadao/f-js-utils';
import { find } from '@fretadao/f-js-dom';

/*
  Example usage in HTML:

<a data-controller="full-screen-continers"
    data-full-screen-continers-container-id-value="operation-confirmation-table"
    data-full-screen-original-classes-value="m-6 py-2 px-4 rounded-md border-2"
    data-action="click->full-screen-continers#toggle"
    href="javascript: void(0);">Full Screen</a>
  <div id="my-container-id">
    Content goes here...
  </div>

  - This controller checks if the URL contains the "full_screen" query parameter.
  - If "full_screen" is present, the target container will get the full-screen styles:
    `fixed inset-0 bg-white shadow-lg z-50 p-8 overflow-auto`.
  - If "full_screen" is not present, it will use the default classes provided in
    `data-full-screen-containers-original-classes-value`.
*/
application.register('full-screen-continers', class extends Controller {
  static targets = ['container'];
  static values = { originalClasses: String, containerId: String };

  connect() {
    this.#defineScreenMode();
    this.#defineBrowserMode();
  };

  toggle() {
    this.#isFullScreen() ? this.#removeFullScreenParameter() : this.#addFullScreenParameter();

    this.#defineScreenMode();
    this.#defineBrowserMode();

  }

  #defineScreenMode() {
    this.#container().className = this.#containerClasses();
  }

  #browserIsInFullScreen() {
    return (document.fullscreenElement && document.fullscreenElement !== null) ||
      (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) ||
      (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
      (document.msFullscreenElement && document.msFullscreenElement !== null);
  }

  // Reference: https://stackoverflow.com/questions/36672561/how-to-exit-fullscreen-onclick-using-javascript
  #defineBrowserMode() {
    const browserIsInFullScreen = this.#browserIsInFullScreen();

    const docElm = document.documentElement;
    if (this.#isFullScreen() && !browserIsInFullScreen) {
      if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
      } else if (docElm.mozRequestFullScreen) {
        docElm.mozRequestFullScreen();
      } else if (docElm.webkitRequestFullScreen) {
        docElm.webkitRequestFullScreen();
      } else if (docElm.msRequestFullscreen) {
        docElm.msRequestFullscreen();
      }
    } else if(browserIsInFullScreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }

  #containerClasses() {
    return this.#isFullScreen() ? this.#fullScreenClasses() : this.originalClassesValue;
  }

  #isFullScreen() {
    return !!this.#urlParams().get('full_screen');
  }

  #urlParams() {
    return (new URL(window.location)).searchParams;
  }

  #fullScreenClasses() {
    return 'fixed inset-0 bg-white shadow-lg z-50 p-8 overflow-auto'
  }

  #container() {
    return find(`#${this.containerIdValue}`);
  }

  #addFullScreenParameter() {
    const url = new URL(window.location);
    const params = url.searchParams;

    if (isPresent(params.get('full_screen'))) return;

    params.set('full_screen', true);

    window.history.pushState({}, "", url);
  }

  #removeFullScreenParameter() {
    const url = new URL(window.location);
    const params = url.searchParams;

    if (isBlank(params.get('full_screen'))) return;

    params.delete('full_screen');

    window.history.pushState({}, "", url);
  }
});
